import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import AdminApi from "Service/Admin.api";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
import { formatDate } from "Utils/Common";
import { StatusChip } from "Pages/OrderHistory/OrderHistory";

interface EMitraCase {
    pkCase: number;
    description: string;
    casePrice: number;
    dateCreated: string;
    createdByUserId: number;
    eMitraName: string;
    eMitraCommissionAmount: number;
    isEMitraCommissionPaid: boolean;
    client: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        contactNumber: string;
    };
}

const EMitraCases: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [searchQuery, setSearchQuery] = useState("");
    const [cases, setCases] = useState<EMitraCase[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedCase, setSelectedCase] = useState<EMitraCase | null>(null);
    const [commissionAmount, setCommissionAmount] = useState<number | string>("");

    const dispatch = useDispatch();

    useEffect(() => {
        console.log("Component mounted, fetching e_Mitra cases...");
        fetchEMitraCases();
    }, []);

    const fetchEMitraCases = async () => {
        dispatch(startLoading({ loading: true, loadtext: "loading e_mitra cases..." }));
        try {
            const response = await AdminApi.getEMitraCases();
            console.log("Fetched e_Mitra cases:", response);    
            if (response && Array.isArray(response.data)) {
                setCases(response.data);
            } else {
                setCases([]);
            }
        } catch (error) {
            console.error("Error fetching e_mitra cases:", error);
            setCases([]);
        } finally {
            dispatch(stopLoading());
        }
    };

    const filteredData = useMemo(() => {
        const normalizedQuery = searchQuery.toLowerCase().trim();
        if (!normalizedQuery) return cases;

        return cases.filter((c) => {
            return (
                c.eMitraName?.toLowerCase().includes(normalizedQuery) ||
                c.pkCase?.toString().toLowerCase().includes(normalizedQuery) ||
                c.client?.firstName?.toLowerCase().includes(normalizedQuery) ||
                c.client?.lastName?.toLowerCase().includes(normalizedQuery)
            );
        });
    }, [searchQuery, cases]);

    const handleOpenDialog = (caseItem: EMitraCase) => {
        setSelectedCase(caseItem);
        setCommissionAmount(caseItem.eMitraCommissionAmount || 0);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedCase(null);
        setCommissionAmount("");
    };

    const handlePayCommission = async () => {
        if (!selectedCase) return;
        dispatch(startLoading({ loading: true, loadtext: "Processing payout..." }));
        try {
            const payload = { commissionAmount: Number(commissionAmount) };
            await AdminApi.payEMitraCommission(selectedCase.pkCase, payload);
            handleCloseDialog();
            fetchEMitraCases();
        } catch (error) {
            console.error("Error paying commission:", error);
        } finally {
            dispatch(stopLoading());
        }
    };

    const renderTable = () => (
        <Paper sx={{ borderRadius: 3, overflow: "hidden", background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)" }}>
            <TableContainer sx={{ maxHeight: "70vh", overflowY: "auto" }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Case No.</StyledTableCell>
                            <StyledTableCell>e_Mitra Name</StyledTableCell>
                            <StyledTableCell>Client Details</StyledTableCell>
                            <StyledTableCell>Case Price</StyledTableCell>
                            <StyledTableCell>Commission</StyledTableCell>
                            <StyledTableCell>Status</StyledTableCell>
                            <StyledTableCell>Created At</StyledTableCell>
                            <StyledTableCell>Action</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                    <Typography variant="h6">No e_Mitra cases found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredData.map((item) => (
                                <StyledTableRow key={item.pkCase}>
                                    <TableCell>{item.pkCase}</TableCell>
                                    <TableCell>{item.eMitraName || `ID: ${item.createdByUserId}`}</TableCell>
                                    <TableCell>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                                                {item.client?.firstName} {item.client?.lastName}
                                            </Typography>
                                            <Typography variant="caption" color="text.primary">
                                                {item.client?.email}
                                            </Typography>
                                            <Typography variant="caption" color="text.primary">
                                                {item.client?.contactNumber}
                                            </Typography>
                                        </div>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.casePrice}</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>{item.eMitraCommissionAmount}</TableCell>
                                    <TableCell>
                                        <StatusChip
                                            label={item.isEMitraCommissionPaid ? "Paid" : "Unpaid"}
                                            status={item.isEMitraCommissionPaid ? "Completed" : "Pending"}
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(item.dateCreated)}</TableCell>
                                    <TableCell>
                                        {!item.isEMitraCommissionPaid && (
                                            <Button variant="contained" color="primary" size="small" onClick={() => handleOpenDialog(item)}>
                                                Pay
                                            </Button>
                                        )}
                                    </TableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    return (
        <Box p={3} width="100%">
            <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                }}
            >
                e_Mitra Cases & Commissions
            </Typography>
            <TextField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search cases..."
                size="small"
                sx={{ width: { xs: "100%", sm: "300px" }, mb: 3 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />
            {isMobile ? <Typography variant="body1">Cards view not implemented for e_Mitra cases.</Typography> : renderTable()}

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Pay e_Mitra Commission</DialogTitle>
                <DialogContent sx={{ minWidth: 300 }}>
                    <Typography variant="body2" gutterBottom>
                        Case No: {selectedCase?.pkCase}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        e_Mitra User: {selectedCase?.eMitraName}
                    </Typography>
                    <TextField
                        margin="dense"
                        label="Commission Amount"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={commissionAmount}
                        onChange={(e) => setCommissionAmount(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handlePayCommission} variant="contained" color="primary">
                        Confirm Payout
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EMitraCases;
